'use client';

import { useGSAP } from '@gsap/react';
import clsx from "clsx";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Image from "next/image";
import { useRef, useMemo, useState } from 'react';

import Reveal from "@/components/animation/reveal";
import AddStoryCard from "@/components/card/add-story-card/add-story-card";
import Cta from "@/components/cta/cta";
import Section from "@/components/section";
import { useApp } from "@/context/AppContext";

// Special item for "Find your story" CTA
const FIND_YOUR_STORY = { _uid: 'find-your-story', type: 'cta' };

// Split array into N columns with fixed rows per column
const splitIntoColumns = (arr, numCols, rowsPerCol, ctaPosition) => {
  let index = 0;
  const columns = Array.from({ length: numCols }, (_, colIndex) => {
    // Column 2 (index 1) gets one fewer item to make room for CTA
    const itemCount = colIndex === 1 ? rowsPerCol - 1 : rowsPerCol;
    const column = arr.slice(index, index + itemCount);
    index += itemCount;
    return column;
  });

  // Insert "Find your story" into column 2 at specified row
  if (columns[1]) {
    columns[1].splice(ctaPosition, 0, FIND_YOUR_STORY);
  }

  return columns;
};

export default function AboutRecords({ items, link, marquee, text, title }) {
  const { isMobile } = useApp();
  // Mobile: 2 cols × 3 rows, CTA at row 2 | Desktop: 3 cols × 2 rows, CTA at row 1
  const columns = useMemo(
    () => splitIntoColumns(items, isMobile ? 2 : 3, isMobile ? 3 : 2, isMobile ? 1 : 0),
    [items, isMobile]
  );
  const [isMarqueeVisible, setIsMarqueeVisible] = useState(false);
  const sectionRef = useRef(null);
  const gridRef = useRef(null);
  const marqueeRef = useRef(null);
  const columnParallaxRefs = useRef([]);

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add('(prefers-reduced-motion: no-preference)', () => {
      // Target inner column divs for enter animation (not the parallax wrappers)
      const columnElements = Array.from(gridRef.current.children).map(wrapper => wrapper.firstElementChild);
      gsap.set(columnElements, { y: 300 });

      ScrollTrigger.create({
        trigger: gridRef.current,
        start: 'top 80%',
        once: true,
        onEnter: () => {
          gsap.to(columnElements, {
            y: 0,
            duration: 1,
            ease: 'power3.out',
            stagger: 0.1,
          });
        },
      });

      // Parallax scrolling on column wrappers (smaller values on mobile)
      const parallaxValues = isMobile
        ? [40, -20] // Mobile: 2 columns
        : [80, -40, 60]; // Desktop: 3 columns
      columnParallaxRefs.current.forEach((el, index) => {
        if (el) {
          gsap.to(el, {
            y: parallaxValues[index] || 0,
            ease: 'none',
            force3D: true,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
              invalidateOnRefresh: true,
            },
          });
        }
      });

      // Pause marquee when out of view
      ScrollTrigger.create({
        trigger: marqueeRef.current,
        start: 'top bottom',
        end: 'bottom top',
        onEnter: () => setIsMarqueeVisible(true),
        onLeave: () => setIsMarqueeVisible(false),
        onEnterBack: () => setIsMarqueeVisible(true),
        onLeaveBack: () => setIsMarqueeVisible(false),
      });
    });
  }, []);

  const addStoryCardLink = {
    href: 'https://ancestry.com/story-scout',
  };

  return (
    <Section ref={sectionRef} theme="main-dark" className="relative pt-14 lg:pt-21.25">
      <div className={`absolute bottom-full left-0 w-full h-4 before:content-[''] before:absolute before:inset-0 before:bg-[url('https://www.ancestrycdn.com/america-250/img/rustic-border.webp')] before:bg-repeat-x after:content-[''] after:absolute after:inset-0 after:bg-[url('https://www.ancestrycdn.com/america-250/img/rustic-border.webp')] after:bg-repeat-x after:scale-x-[-1]`} aria-hidden="true" />
      <div className="flex flex-col items-center">
        <div className="grid-7 items-center">
          <Reveal className="col-span-full max-w-98.25 lg:col-span-3 max-lg:mx-auto">
            <h2 className="heading-3-small max-w-73 md:max-w-[80%] max-lg:mx-auto text-center xl:max-w-none lg:heading-3 lg:text-left">{title}</h2>
            <p className="mt-6 body-1-small text-center text-pretty xl:body-2 lg:mt-8 lg:max-w-82.75 lg:text-left">{text}</p>
            <Cta href={link.href} target={link.target} className="w-fit mx-auto mt-8 cta-big lg:mx-0">{link.label}</Cta>
          </Reveal>

          <div ref={gridRef} className="grid grid-cols-2 gap-x-2.5 gap-y-5 col-span-full mt-14 lg:grid-cols-3 lg:gap-x-4.25 lg:gap-y-8 lg:col-span-4 lg:mt-0">
            {columns.map((column, colIndex) => (
              <div
                key={colIndex}
                ref={(el) => (columnParallaxRefs.current[colIndex] = el)}
                className="will-change-transform"
              >
                <div className={clsx("flex flex-col gap-5 lg:gap-8", colIndex === 1 && "mt-12.5 lg:mt-16.875", colIndex === 2 && "lg:mt-9")}>
                  {column.map((item) =>
                    item.type === 'cta' ? (
                      <AddStoryCard key={item._uid} text="Find your story" isSmall={true} link={addStoryCardLink} />
                    ) : (
                      <Image unoptimized={true} key={item._uid} {...item.image} className="w-full" loading="lazy" />
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div ref={marqueeRef} className="group marquee mt-14 pt-9 pb-11 lg:mt-20 lg:pt-11 focus-within:[animation-play-state:paused]" role="region" aria-label="Featured stories marquee">
        {/* Duplicate content for seamless loop */}
        {[...Array(2)].map((_, i) => (
          <div key={i} className={clsx("marquee-content gap-x-4 pr-4 lg:gap-x-6 lg:pr-6 group-hover:[animation-play-state:paused] group-focus-within:[animation-play-state:paused]", !isMarqueeVisible && "[animation-play-state:paused]")} aria-hidden={i > 0} {...(i > 0 && { tabIndex: -1 })}>
            {marquee.map(item => (
              <div key={item._uid} className="flex items-center gap-x-4 lg:gap-x-6" role="listitem" tabIndex={i === 0 ? 0 : -1}>
                <p className="heading-3-small xl:heading-2 whitespace-nowrap">{item.text}</p>
                <div className="relative overflow-hidden rounded-[50%] w-12 aspect-[80/104] border border-main-light lg:w-20">
                  <Image unoptimized={true} className="absolute object-cover top-0 left-0 w-full h-full" {...item.image} alt="" loading="lazy" />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className={`absolute top-full left-0 w-full h-4 scale-y-[-1] before:content-[''] before:absolute before:inset-0 before:bg-[url('https://www.ancestrycdn.com/america-250/img/rustic-border.webp')] before:bg-repeat-x after:content-[''] after:absolute after:inset-0 after:bg-[url('https://www.ancestrycdn.com/america-250/img/rustic-border.webp')] after:bg-repeat-x after:scale-x-[-1]`} aria-hidden="true" />
    </Section>
  )
}
